import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getSetting, updateSetting, initializeDatabase } from '../services/database';
import { getLocalTranslations, getBooks, getChapter as getLocalChapter, getVerseOfTheDay } from '../services/localBibleApi';
import { parseNavigationFromPath, isResumeReadingPath } from '../constants/bibleConstants';

const BibleContext = createContext(null);

export function BibleProvider({ children }) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Bible data state
  const [availableBibles, setAvailableBibles] = useState([]);
  const [selectedTranslations, setSelectedTranslations] = useState([]);
  const [currentBook, setCurrentBook] = useState(null);
  const [currentChapter, setCurrentChapter] = useState(1);
  const [books, setBooks] = useState([]);
  const [chapterContent, setChapterContent] = useState({});
  const [verseOfTheDay, setVerseOfTheDay] = useState(null);
  
  // UI state
  const [viewMode, setViewMode] = useState('single'); // 'single', 'parallel', 'stacked'
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  
  // Initialize
  useEffect(() => {
    async function init() {
      try {
        await initializeDatabase();
        
        // Load available local translations
        const localTranslations = await getLocalTranslations();
        console.log('BibleContext: Local translations loaded:', localTranslations.length);
        
        // Only use local translations since no API key is configured
        // API translations would require REACT_APP_API_BIBLE_KEY in .env file
        const allTranslations = [...localTranslations];
        console.log('BibleContext: Total translations available:', allTranslations.length);
        setAvailableBibles(allTranslations);
        
        // Load saved translations preference
        const defaultTranslations = await getSetting('defaultTranslations');
        let primaryTranslation = 'eng_niv'; // Default to English NIV
        
        // Always prioritize English NIV if available, then KJV
        const niv = allTranslations.find(t => t.id === 'eng_niv');
        const kjv = allTranslations.find(t => t.id === 'eng_kjv');
        
        if (defaultTranslations && defaultTranslations.length > 0) {
          // Filter to only include available translations
          const validTranslations = defaultTranslations.filter(
            t => allTranslations.some(tr => tr.id === t)
          );
          if (validTranslations.length > 0) {
            setSelectedTranslations(validTranslations);
            primaryTranslation = validTranslations[0];
          } else if (niv) {
            setSelectedTranslations([niv.id]);
            primaryTranslation = niv.id;
          } else if (kjv) {
            setSelectedTranslations([kjv.id]);
            primaryTranslation = kjv.id;
          } else if (allTranslations.length > 0) {
            setSelectedTranslations([allTranslations[0].id]);
            primaryTranslation = allTranslations[0].id;
          }
        } else if (niv) {
          // Default to English NIV
          primaryTranslation = niv.id;
          setSelectedTranslations([primaryTranslation]);
        } else if (kjv) {
          // Fallback to English KJV
          primaryTranslation = kjv.id;
          setSelectedTranslations([primaryTranslation]);
        } else if (allTranslations.length > 0) {
          setSelectedTranslations([allTranslations[0].id]);
          primaryTranslation = allTranslations[0].id;
        }
        
        // Load books for primary translation
        console.log('BibleContext: Loading books for translation:', primaryTranslation);
        // All translations now use local files
        const bookList = getBooks(primaryTranslation);
        console.log('BibleContext: Books loaded:', bookList.length);
        if (bookList.length === 0) {
          console.error('BibleContext: No books loaded! This will break the app.');
        }
        setBooks(bookList);
        
        // Explicit /reader/... or /bible/... URLs always beat saved reading position.
        const urlPath = window.location.pathname;
        const urlNavigation = parseNavigationFromPath(urlPath);

        if (urlNavigation?.bookId && bookList.length > 0) {
          const urlBook = bookList.find(
            (b) => b.id.toLowerCase() === urlNavigation.bookId.toLowerCase()
          );
          if (urlBook) {
            const maxChapter = urlBook.chapters || 150;
            const validChapter =
              urlNavigation.chapter >= 1 && urlNavigation.chapter <= maxChapter
                ? urlNavigation.chapter
                : 1;
            console.log('BibleContext: URL navigation - setting', urlBook.id, 'chapter', validChapter);
            setCurrentBook(urlBook);
            setCurrentChapter(validChapter);
          } else {
            console.warn('BibleContext: URL book not found:', urlNavigation.bookId);
            setCurrentBook(bookList[0]);
            setCurrentChapter(1);
          }
        } else if (isResumeReadingPath(urlPath)) {
          const savedPosition = await getSetting('readingPosition');
          if (savedPosition?.bookId && bookList.length > 0) {
            const savedBook = bookList.find((b) => b.id === savedPosition.bookId);
            if (savedBook) {
              setCurrentBook(savedBook);
              setCurrentChapter(savedPosition.chapter || 1);
            } else {
              setCurrentBook(bookList[0]);
              setCurrentChapter(1);
            }
          } else if (bookList.length > 0) {
            setCurrentBook(bookList[0]);
            setCurrentChapter(1);
          }
        } else if (bookList.length > 0) {
          setCurrentBook(bookList[0]);
          setCurrentChapter(1);
        }
        
        // Load verse of the day
        const votd = await getVerseOfTheDay(primaryTranslation);
        setVerseOfTheDay(votd);
        
        setIsLoading(false);
      } catch (err) {
        console.error('Failed to initialize Bible context:', err);
        setError(err.message);
        setIsLoading(false);
      }
    }
    
    init();
  }, []);
  
  // Update books when primary translation changes
  useEffect(() => {
    if (selectedTranslations.length > 0) {
      const primaryTranslation = selectedTranslations[0];
      // All translations now use local files
      const bookList = getBooks(primaryTranslation);
      setBooks(bookList);
    }
  }, [selectedTranslations]);
  
  // Monitor online status
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  // Load chapter content for all selected translations
  const loadChapterContent = useCallback(async (bookId, chapter) => {
    if (!bookId || !chapter) return;
    
    console.log(`Loading chapter content: ${bookId} ${chapter} for translations:`, selectedTranslations);
    setIsLoading(true);
    const content = {};
    
    try {
      await Promise.all(
        selectedTranslations.map(async (translationId) => {
          try {
            console.log(`Loading ${translationId} ${bookId} ${chapter}`);
            // All translations now use local files
            const chapterData = await getLocalChapter(translationId, bookId, chapter);
            
            console.log(`Successfully loaded ${translationId}:`, chapterData?.verses?.length || 0, 'verses');
            content[translationId] = chapterData;
          } catch (err) {
            console.error(`Failed to load ${translationId} ${bookId} ${chapter}:`, err);
            content[translationId] = { verses: [], error: err.message };
          }
        })
      );
      
      console.log('Chapter content loaded:', Object.keys(content));
      setChapterContent(content);
    } catch (err) {
      console.error('Failed to load chapter content:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [selectedTranslations]);
  
  // Load chapter when current book/chapter changes
  useEffect(() => {
    if (currentBook?.id && currentChapter) {
      loadChapterContent(currentBook.id, currentChapter);
    }
  }, [currentBook, currentChapter, loadChapterContent]);
  
  // Save reading position when book or chapter changes
  useEffect(() => {
    if (currentBook?.id && currentChapter && !isLoading) {
      updateSetting('readingPosition', {
        bookId: currentBook.id,
        chapter: currentChapter
      }).catch(err => {
        console.warn('Failed to save reading position:', err);
      });
    }
  }, [currentBook, currentChapter, isLoading]);
  
  // Navigation functions
  const goToBook = useCallback((bookId, chapter = 1) => {
    if (!bookId) return;
    const book = books.find(b => b.id === bookId);
    if (!book) {
      console.warn(`Book not found: ${bookId}`);
      return;
    }
    
    // Validate chapter number
    const chapterNum = parseInt(chapter);
    const maxChapter = book.chapters || 150;
    const validChapter = (!isNaN(chapterNum) && chapterNum >= 1 && chapterNum <= maxChapter) 
      ? chapterNum 
      : 1;
    
    // Set both book and chapter atomically to avoid intermediate renders
    setCurrentBook(book);
    setCurrentChapter(validChapter);
  }, [books]);
  
  const goToChapter = useCallback((chapterNum) => {
    const num = parseInt(chapterNum);
    if (isNaN(num) || num < 1) return;
    
    if (currentBook) {
      const maxChapter = currentBook.chapters || 150; // Default max for safety
      if (num <= maxChapter) {
        setCurrentChapter(num);
      }
    } else {
      // If no book selected yet, just set the chapter
      setCurrentChapter(num);
    }
  }, [currentBook]);
  
  const goToNextChapter = useCallback(() => {
    if (!currentBook) return;
    
    if (currentChapter < currentBook.chapters) {
      // Next chapter in same book
      setCurrentChapter(currentChapter + 1);
    } else {
      // Go to next book
      const bookIndex = books.findIndex(b => b.id === currentBook.id);
      if (bookIndex < books.length - 1) {
        const nextBook = books[bookIndex + 1];
        setCurrentBook(nextBook);
        setCurrentChapter(1);
      }
    }
  }, [currentChapter, currentBook, books]);
  
  const goToPreviousChapter = useCallback(() => {
    if (!currentBook) return;
    
    if (currentChapter > 1) {
      // Previous chapter in same book
      setCurrentChapter(currentChapter - 1);
    } else {
      // Go to previous book
      const bookIndex = books.findIndex(b => b.id === currentBook.id);
      if (bookIndex > 0) {
        const prevBook = books[bookIndex - 1];
        setCurrentBook(prevBook);
        setCurrentChapter(prevBook.chapters); // Last chapter of previous book
      }
    }
  }, [currentChapter, currentBook, books]);
  
  // Go to specific reference (e.g., "John 3:16" or "Gen.1.1")
  const goToReference = useCallback((reference) => {
    // Parse reference like "John 3:16" or "Gen 1:16" or "Gen.1.16"
    let bookId, chapter;
    
    // Try OSIS format first (Gen.1.16)
    const osisMatch = reference.match(/^(\d?[A-Za-z]+)\.(\d+)/);
    if (osisMatch) {
      bookId = osisMatch[1];
      chapter = parseInt(osisMatch[2]);
    } else {
      // Try human format (Genesis 1:16 or Gen 1:16)
      const humanMatch = reference.match(/^(\d?\s*[A-Za-z]+)\s*(\d+)/);
      if (humanMatch) {
        const bookName = humanMatch[1].trim();
        chapter = parseInt(humanMatch[2]);
        
        // Find book by name or abbreviation
        const book = books.find(b => 
          b.name.toLowerCase().startsWith(bookName.toLowerCase()) ||
          b.id.toLowerCase() === bookName.toLowerCase()
        );
        if (book) {
          bookId = book.id;
        }
      }
    }
    
    if (bookId && chapter) {
      goToBook(bookId);
      setTimeout(() => goToChapter(chapter), 0);
    }
  }, [books, goToBook, goToChapter]);
  
  // Translation management
  const addTranslation = useCallback((translationId) => {
    if (!selectedTranslations.includes(translationId) && selectedTranslations.length < 4) {
      const newTranslations = [...selectedTranslations, translationId];
      setSelectedTranslations(newTranslations);
      updateSetting('defaultTranslations', newTranslations);
    }
  }, [selectedTranslations]);
  
  const removeTranslation = useCallback((translationId) => {
    if (selectedTranslations.length > 1) {
      const newTranslations = selectedTranslations.filter(id => id !== translationId);
      setSelectedTranslations(newTranslations);
      updateSetting('defaultTranslations', newTranslations);
    }
  }, [selectedTranslations]);
  
  const setTranslations = useCallback((translationIds) => {
    if (translationIds.length > 0 && translationIds.length <= 4) {
      setSelectedTranslations(translationIds);
      updateSetting('defaultTranslations', translationIds);
    }
  }, []);
  
  // Get translation info
  const getTranslationInfo = useCallback((translationId) => {
    return availableBibles.find(b => b.id === translationId);
  }, [availableBibles]);
  
  // Get chapters list for current book
  const getChaptersList = useCallback(() => {
    if (!currentBook) return [];
    return Array.from({ length: currentBook.chapters }, (_, i) => ({
      number: i + 1,
      id: `${currentBook.id}.${i + 1}`
    }));
  }, [currentBook]);
  
  const value = {
    isLoading,
    error,
    isOffline,
    
    // Bible data
    availableBibles,
    selectedTranslations,
    currentBook,
    currentChapter,
    books,
    chapterContent,
    verseOfTheDay,
    
    // UI state
    viewMode,
    setViewMode,
    
    // Navigation
    goToBook,
    goToChapter,
    goToNextChapter,
    goToPreviousChapter,
    goToReference,
    getChaptersList,
    
    // Translation management
    addTranslation,
    removeTranslation,
    setTranslations,
    getTranslationInfo,
    
    // Utilities
    loadChapterContent
  };
  
  return (
    <BibleContext.Provider value={value}>
      {children}
    </BibleContext.Provider>
  );
}

export function useBible() {
  const context = useContext(BibleContext);
  if (!context) {
    throw new Error('useBible must be used within a BibleProvider');
  }
  return context;
}

export default BibleContext;
